import type { ScenarioInput, ScenarioResult } from './scenario-run';
import type { RunChannel, ValidateRun } from './validate-runs';
import {
  caseIsScripted,
  sessionContextOf,
  type SuiteTask,
  type ValidateSuite,
} from './validate-suites';

/**
 * One task of a suite run, as the runner takes it and as the runs list keeps
 * it — shared by the page (Run suite) and the server (a schedule), so a run
 * reads the same whoever started it.
 */

export const scenarioInputOf = (
  task: SuiteTask,
  suite: ValidateSuite,
  channel: RunChannel
): ScenarioInput => ({
  channel,
  description: task.case.description.trim() || task.case.name.trim(),
  expectation: task.case.expectation,
  maxTurns: task.case.maxTurns,
  startAt: task.case.startAt,
  ...(caseIsScripted(task.case) ? { script: task.case.script } : {}),
  context: sessionContextOf(suite.context),
  model: task.model,
  mustSay: task.case.mustSay,
  mustNotSay: task.case.mustNotSay,
});

export const runOfSuiteTask = ({
  id,
  task,
  suite,
  suiteRunId,
  channel,
  versionLabel,
  result,
  scheduleId,
}: {
  id: string;
  task: SuiteTask;
  suite: ValidateSuite;
  suiteRunId: string;
  channel: RunChannel;
  versionLabel: string | null;
  result: ScenarioResult;
  /** Set when a schedule ran it, not a person. */
  scheduleId?: string;
}): ValidateRun => ({
  id,
  kind: 'scenario',
  title:
    task.case.description.trim() ||
    task.case.name.trim() ||
    (task.case.script ?? '').split('\n')[0],
  checks: result.checks,
  ...(task.model ? { model: task.model } : {}),
  expectation: task.case.expectation,
  expectedAgent: task.case.expectedAgent ?? null,
  startAt: task.case.startAt,
  channel,
  versionLabel,
  chatId: result.chatId,
  transcript: result.transcript,
  focusIndex: null,
  startedAt: result.startedAt,
  endedAt: result.endedAt,
  score: result.score,
  turnScores: result.turnScores,
  stoppedBecause: result.stoppedBecause,
  suite: {
    suiteId: suite.id,
    suiteRunId,
    suiteName: suite.name,
    caseId: task.case.id,
    caseName: task.case.name.trim() || task.case.description,
    iteration: task.iteration,
    ...(scheduleId ? { scheduleId } : {}),
  },
});
